import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import { resolveBoardConfig } from './communityConfig';
import { supabase } from '../../lib/supabaseClient';

const CommunityDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const boardKey = String(params?.board || 'matching');
  const postId = String(params?.postId || '');
  const config = resolveBoardConfig(boardKey);

  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [authUser, setAuthUser] = useState(null);

  const canEdit = useMemo(() => {
    if (!authUser || !selectedItem) return false;
    return authUser.id === selectedItem.author_id;
  }, [authUser, selectedItem]);

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      setLoadError('');
      const { data: sessionData } = await supabase.auth.getSession();
      setAuthUser(sessionData?.session?.user || null);

      try {
        const { data, error } = await supabase
          .from('community_posts')
          .select('id, board_key, title, content, author_id, author_name, image_urls, view_count, created_at, updated_at, neighborhood_name')
          .eq('id', postId)
          .maybeSingle();
        if (error || !data) {
          setLoadError(error?.message || '게시글을 찾을 수 없습니다.');
          setSelectedItem(null);
          setLoading(false);
          return;
        }

        setSelectedItem(data);
      } catch (err) {
        setLoadError(err?.message || '게시글을 불러오지 못했습니다.');
        setSelectedItem(null);
      } finally {
        setLoading(false);
      }
    };

    if (postId) loadDetail();
  }, [postId]);

  const handleDelete = async () => {
    if (!canEdit) {
      alert('본인의 글만 삭제할 수 있습니다.');
      return;
    }
    const ok = window.confirm('정말 삭제하시겠습니까?');
    if (!ok) return;
    const { error } = await supabase
      .from('community_posts')
      .delete()
      .eq('id', postId)
      .eq('author_id', authUser?.id);
    if (error) {
      alert(error?.message || '삭제에 실패했습니다.');
      return;
    }
    navigate(`/openplay/community/${boardKey}`);
  };

  if (loading) {
    return (
      <OpenPlayToolsLayout userRole="player">
        <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-10">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="bg-card border border-border rounded-xl p-6 text-center">
              <p className="text-sm text-muted-foreground">불러오는 중...</p>
            </div>
          </div>
        </main>
      </OpenPlayToolsLayout>
    );
  }

  if (!selectedItem) {
    return (
      <OpenPlayToolsLayout userRole="player">
        <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-10">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="bg-card border border-border rounded-xl p-6 text-center">
              <p className="text-sm text-muted-foreground">{loadError || '게시글을 찾을 수 없습니다.'}</p>
              <Link
                to={`/openplay/community/${boardKey}`}
                className="inline-flex mt-4 h-10 px-4 rounded-lg border border-border text-sm text-muted-foreground items-center justify-center"
              >
                목록으로
              </Link>
            </div>
          </div>
        </main>
      </OpenPlayToolsLayout>
    );
  }

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-10">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">{config.label}</h1>
            <p className="text-sm text-muted-foreground">게시글 상세</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <div>
              <p className="text-xs text-muted-foreground">제목</p>
              <h2 className="text-xl font-semibold text-foreground mt-1">{selectedItem.title}</h2>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span>작성자: {selectedItem.author_name}</span>
              <span>조회수: {selectedItem.view_count || 0}</span>
              {(boardKey === 'matching' || boardKey === 'free') && selectedItem.neighborhood_name && (
                <span>위치: {selectedItem.neighborhood_name}</span>
              )}
              <span>작성일: {new Date(selectedItem.created_at).toLocaleString()}</span>
              {selectedItem.updated_at && <span>수정일: {new Date(selectedItem.updated_at).toLocaleString()}</span>}
            </div>
            <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{selectedItem.content}</div>

            {Array.isArray(selectedItem.image_urls) && selectedItem.image_urls.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {selectedItem.image_urls.map((url) => (
                  <div key={url} className="border border-border rounded-lg overflow-hidden">
                    <img src={url} alt="community" className="w-full h-28 object-cover" />
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <Link
                to={`/openplay/community/${boardKey}`}
                className="h-10 px-4 rounded-lg border border-border text-sm text-muted-foreground flex items-center justify-center"
              >
                목록
              </Link>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <Link
                    to={`/openplay/community/${boardKey}/${selectedItem.id}/edit`}
                    className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center"
                  >
                    수정
                  </Link>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="h-10 px-4 rounded-lg border border-border text-sm text-destructive"
                  >
                    삭제
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </OpenPlayToolsLayout>
  );
};

export default CommunityDetailPage;
